/**
 * The custom-field renderer — Step 28/29's shared piece. ONE component
 * renders every field type, walking fields in order and evaluating
 * `showWhen` against values-so-far exactly like `validateFieldValues` does
 * server-side (same `@itsm/shared` evaluator) — so what the requester sees
 * can never disagree with what the server enforces. Used by the field
 * builder's live preview and the real catalogue submission form alike.
 */
import { useEffect, useMemo, useState } from 'react';
import { CircleUserRound, Plus, X } from 'lucide-react';
import { FileFieldInput } from './FileFieldInput';
import {
  evaluateCondition,
  formatFieldValue,
  type Condition,
  type FileFieldValue,
  type ResolvedField,
  type SelectOption,
} from '@itsm/shared';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select } from '../../components/ui/select';
import { RichTextEditor } from '../../components/ui/richtext';
import { apiFetch } from '../../lib/api';
import { portalFetch } from '../../lib/portalApi';
import { cn } from '../../lib/utils';
import type { Person } from '../tickets/api';

/** `select`/`multiselect` fields configured with `peopleFilter` get their
 *  options from the directory instead of a hand-entered list — resolved
 *  once per distinct filter, not on every keystroke. */
function useDirectoryOptions(filter: Condition | undefined): SelectOption[] | null {
  const [options, setOptions] = useState<SelectOption[] | null>(null);
  useEffect(() => {
    if (!filter) {
      setOptions(null);
      return;
    }
    let cancelled = false;
    apiFetch<{ entries: Person[] }>('/api/org/people/match', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filter }),
    })
      .then((res) => {
        if (!cancelled) setOptions(res.entries.map((p) => ({ value: p.id, label: p.name ?? p.email })));
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [filter]);
  return options;
}

/** A `person` field's stored value — `id` is null for someone typed in
 *  free-text rather than picked from the directory (e.g. a new starter who
 *  has no account yet). Distinct from ticket `Person`, which always names a
 *  real existing user. */
export interface PersonFieldValue {
  id: string | null;
  name: string;
  email: string | null;
}

export interface FieldRendererProps {
  fields: ResolvedField[];
  values: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  errors?: Record<string, string>;
  /** 'portal' on the REQUESTER PORTAL: person fields search through the
   *  portal's own people endpoint (never an agent-domain route — three doors
   *  rule) and directory-sourced dropdowns rely on server-resolved options. */
  surface?: 'agent' | 'portal';
}

function PersonPicker({
  value,
  onChange,
  surface = 'agent',
}: {
  value: PersonFieldValue | null;
  onChange: (p: PersonFieldValue | null) => void;
  surface?: 'agent' | 'portal';
}) {
  // URL state on purpose: a people-picker's search box inside one field of a
  // form. Putting it in the URL would leak a half-typed name into a shared link.
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Person[]>([]);

  async function search(q: string) {
    setQuery(q);
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    try {
      const term = encodeURIComponent(q.trim());
      // Each domain searches through ITS OWN door (Kai: the on-behalf picker
      // searches all requesters and agents — on the portal too).
      const res =
        surface === 'portal'
          ? await portalFetch<{ entries: Person[] }>(`/api/portal/people?q=${term}`)
          : await apiFetch<{ entries: Person[] }>(`/api/org/people?q=${term}`);
      setResults(res.entries);
    } catch {
      setResults([]);
    }
  }

  if (value) {
    return (
      <div className="flex items-center justify-between rounded-md border bg-muted/40 px-3 py-2 text-sm">
        <span className="flex min-w-0 items-center gap-2">
          <CircleUserRound className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="truncate">{value.name || value.email}</span>
          {!value.id && (
            <span className="shrink-0 rounded bg-brand/15 px-1.5 text-[10px] font-medium text-brand">
              new
            </span>
          )}
        </span>
        <button onClick={() => onChange(null)} className="text-muted-foreground hover:text-foreground">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }
  const q = query.trim();
  return (
    <div className="relative">
      <Input
        value={query}
        onChange={(e) => search(e.target.value)}
        placeholder="Search people, or type a name…"
      />
      {(results.length > 0 || q.length >= 2) && (
        <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-md border bg-popover shadow-md">
          {results.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => {
                onChange({ id: p.id, name: p.name ?? '', email: p.email });
                setQuery('');
                setResults([]);
              }}
              className="flex w-full items-center gap-2 truncate px-2.5 py-1.5 text-left text-sm hover:bg-accent"
            >
              <CircleUserRound className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate">{p.name ?? p.email}</span>
            </button>
          ))}
          {q.length >= 2 && (
            <button
              type="button"
              className={cn(
                'flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-sm hover:bg-accent',
                results.length > 0 && 'border-t',
              )}
              onClick={() => {
                onChange({ id: null, name: q, email: null });
                setQuery('');
                setResults([]);
              }}
            >
              <Plus className="h-3.5 w-3.5 shrink-0 text-brand" />
              <span>
                Use “<span className="font-medium">{q}</span>”
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function TextListInput({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  return (
    <div className="space-y-1.5">
      {value.map((v, i) => (
        <div key={i} className="flex gap-1.5">
          <Input value={v} onChange={(e) => onChange(value.map((x, j) => (j === i ? e.target.value : x)))} />
          <button
            onClick={() => onChange(value.filter((_, j) => j !== i))}
            className="shrink-0 text-muted-foreground hover:text-foreground"
            aria-label="Remove"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button
        onClick={() => onChange([...value, ''])}
        className="flex items-center gap-1 text-xs text-brand hover:underline"
      >
        <Plus className="h-3 w-3" /> Add another
      </button>
    </div>
  );
}

export function FieldRenderer({ fields, values, onChange, errors, surface = 'agent' }: FieldRendererProps) {
  // Same walk-in-order, evaluate-as-you-go logic as validateFieldValues —
  // a showWhen may only reference an EARLIER field, so this is safe.
  const visibility = useMemo(() => {
    const seen: Record<string, unknown> = {};
    const vis: Record<string, boolean> = {};
    for (const f of fields) {
      const key = f.definition.key;
      vis[key] = !f.showWhen || evaluateCondition(f.showWhen, seen);
      seen[key] = values[key];
    }
    return vis;
  }, [fields, values]);

  return (
    <div className="space-y-4">
      {fields.map((f) => {
        const key = f.definition.key;
        if (!visibility[key]) return null;
        if (f.definition.type === 'content') {
          return (
            <div
              key={key}
              className="prose prose-sm dark:prose-invert max-w-none overflow-x-auto rounded-md border bg-muted/20 p-3"
              dangerouslySetInnerHTML={{ __html: f.definition.helpText ?? f.definition.label }}
            />
          );
        }
        const value = values[key];
        const error = errors?.[key];
        // Step 140 — one id per field ties label, help and error together.
        const controlId = `field-${key}`;
        const helpId = f.definition.helpText ? `${controlId}-help` : undefined;
        const errorId = error ? `${controlId}-error` : undefined;
        const describedBy = [helpId, errorId].filter(Boolean).join(' ') || undefined;
        // Step 66's behaviour matrix: requester-visible but not
        // requester-editable renders as a plain read-out here. The server
        // drops any value submitted for it regardless — this is the
        // courtesy, not the control.
        const readOnly = surface === 'portal' && f.requesterCanEdit === false;
        return (
          <div key={key}>
            <label htmlFor={controlId} className="mb-1 block text-sm font-medium">
              {f.definition.label}
              {f.required && !readOnly && <span className="ml-0.5 text-destructive">*</span>}
              {readOnly && (
                <span className="ml-1.5 text-xs font-normal text-muted-foreground">(view only)</span>
              )}
            </label>
            {f.definition.helpText && (
              <p id={helpId} className="mb-1 text-xs text-muted-foreground">
                {f.definition.helpText}
              </p>
            )}
            {readOnly ? (
              <p className="rounded-md border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
                {formatFieldValue(f.definition, value) || '—'}
              </p>
            ) : (
              <FieldControl
                field={f}
                value={value}
                onChange={(v) => onChange(key, v)}
                surface={surface}
                a11y={{
                  id: controlId,
                  'aria-describedby': describedBy,
                  'aria-invalid': error ? true : undefined,
                  'aria-required': f.required ? true : undefined,
                }}
              />
            )}
            {error && (
              <p id={errorId} role="alert" className="mt-1 text-xs text-destructive">
                {error}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * A single-choice field, with the "Other…" escape hatch — rebuilt 2026-09-08.
 *
 * ── The bug this replaces ──────────────────────────────────────────────
 *
 * Kai: *"on the requester portal it doesn't let me enter another value"*, and
 * *"if other is selected, I need to be able to select something else"*.
 *
 * The old version INFERRED "are we in Other mode?" from the value — it was
 * true when the value was non-empty and matched no option. Picking "Other…"
 * sets the value to empty, so the inference immediately said "not Other": the
 * dropdown snapped back to "Select…" and the text box rendered with a
 * `hidden` class. Other was unreachable on every surface, not just the
 * portal; a value could only get there by arriving pre-set from the server.
 *
 * The fix is to stop inferring. Other-mode is explicit state, so "Other with
 * nothing typed yet" is a real state the control can hold and show, instead
 * of being indistinguishable from "nothing selected".
 *
 * ── Other never becomes a silent answer ────────────────────────────────
 *
 * Kai: *"Other shouldn't be an option without requiring me to write an
 * option"*. While the box is empty the stored value stays EMPTY rather than
 * the literal "Other" — so a required field is still blocked by the ordinary
 * required check, and an optional one records no answer rather than the
 * useless word "Other". Nothing is written until somebody types.
 */
function ChoiceInput({
  options: directoryOptions,
  config,
  value,
  onChange,
}: {
  options?: SelectOption[] | undefined;
  config: ResolvedField['definition']['config'];
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  // Archived options leave the picker but must still RENDER on a ticket that
  // already holds one — otherwise the answer silently reads blank and the
  // history is rewritten (plan: "archived, never deleted").
  const live = directoryOptions ?? (config.options ?? []).filter((o) => !o.archived);
  const heldArchived =
    !directoryOptions && value && !live.some((o) => o.value === value)
      ? (config.options ?? []).find((o) => o.value === value && o.archived)
      : undefined;
  const options = heldArchived ? [...live, heldArchived] : live;

  // A value that matches no option can only have come from Other — that is
  // how an existing ticket reopens into the right mode.
  const valueIsOther = !!config.allowOther && !!value && !options.some((o) => o.value === value);
  const [otherMode, setOtherMode] = useState(valueIsOther);
  // Re-sync when the field is re-rendered against a different stored value
  // (switching ticket, resetting a form) — but never fight the person typing.
  useEffect(() => {
    if (valueIsOther) setOtherMode(true);
  }, [valueIsOther]);

  return (
    <div className="space-y-1.5">
      <Select
        value={otherMode ? '__other__' : ((value as string) ?? '')}
        onChange={(e) => {
          const picked = e.target.value;
          if (picked === '__other__') {
            setOtherMode(true);
            // Clear a previously chosen option: the answer is whatever gets
            // typed next, and leaving the old one behind would submit it.
            onChange('');
            return;
          }
          // Choosing a real option (or "Select…") LEAVES Other mode — this is
          // the half that was impossible before.
          setOtherMode(false);
          onChange(picked);
        }}
      >
        <option value="">Select…</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
            {o.archived ? ' (no longer available)' : ''}
          </option>
        ))}
        {config.allowOther && <option value="__other__">Other…</option>}
      </Select>
      {otherMode && (
        <Input
          autoFocus
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Please specify"
        />
      )}
    </div>
  );
}

/** Step 140 — the attributes that tie a control to its label, help and error
 *  for assistive tech. Spread onto the native-backed primitives; the rich
 *  editor and the token pickers manage their own. */
type A11y = {
  id: string;
  'aria-describedby'?: string | undefined;
  'aria-invalid'?: boolean | undefined;
  'aria-required'?: boolean | undefined;
};

function FieldControl({
  field,
  value,
  onChange,
  surface = 'agent',
  a11y,
}: {
  field: ResolvedField;
  value: unknown;
  onChange: (v: unknown) => void;
  surface?: 'agent' | 'portal';
  a11y?: A11y;
}) {
  const { definition: d } = field;
  const c = d.config;
  const directoryOptions = useDirectoryOptions(
    surface === 'agent' && (d.type === 'select' || d.type === 'multiselect') ? c.peopleFilter : undefined,
  );
  switch (d.type) {
    case 'text':
    case 'url':
    case 'email':
    case 'phone':
      return (
        <Input
          {...a11y}
          type={d.type === 'email' ? 'email' : d.type === 'url' ? 'url' : 'text'}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={c.placeholder}
        />
      );
    case 'textarea':
      return (
        <Textarea
          {...a11y}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={c.placeholder}
        />
      );
    case 'richtext':
      return (
        <RichTextEditor
          value={(value as string) ?? ''}
          onChange={onChange}
          {...(c.placeholder ? { placeholder: c.placeholder } : {})}
        />
      );
    case 'number':
    case 'decimal':
      return (
        <Input
          {...a11y}
          type="number"
          value={value === undefined || value === null ? '' : String(value)}
          onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
        />
      );
    case 'checkbox':
      return (
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          className="h-4 w-4 rounded border-input"
        />
      );
    case 'date':
    case 'datetime':
      return (
        <input
          type={d.type === 'date' || c.dateOnly ? 'date' : 'datetime-local'}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:[color-scheme:dark]"
        />
      );
    case 'select':
    case 'radio':
      return (
        <ChoiceInput options={directoryOptions ?? undefined} config={c} value={value} onChange={onChange} />
      );
    case 'multiselect': {
      const live = directoryOptions ?? (c.options ?? []).filter((o) => !o.archived);
      const selected = Array.isArray(value) ? (value as string[]) : [];
      // Same rule as select: an archived choice still shows while it is held.
      const heldArchived = directoryOptions
        ? []
        : (c.options ?? []).filter(
            (o) => o.archived && selected.includes(o.value) && !live.some((l) => l.value === o.value),
          );
      const options = [...live, ...heldArchived];
      return (
        <div className="flex flex-wrap gap-1.5">
          {options.map((o) => {
            const active = selected.includes(o.value);
            return (
              <button
                key={o.value}
                type="button"
                onClick={() =>
                  onChange(active ? selected.filter((v) => v !== o.value) : [...selected, o.value])
                }
                className={`rounded-md border px-2.5 py-1 text-xs ${
                  o.archived
                    ? 'border-dashed text-muted-foreground/70'
                    : active
                      ? 'border-brand bg-brand/10 text-brand'
                      : 'text-muted-foreground hover:bg-accent'
                }`}
                title={o.archived ? 'No longer available' : undefined}
              >
                {o.label}
                {o.archived ? ' (no longer available)' : ''}
              </button>
            );
          })}
        </div>
      );
    }
    case 'text_list':
      return <TextListInput value={Array.isArray(value) ? (value as string[]) : []} onChange={onChange} />;
    case 'person':
      return (
        <PersonPicker value={(value as PersonFieldValue) ?? null} onChange={onChange} surface={surface} />
      );
    case 'file':
      return (
        <FileFieldInput
          value={Array.isArray(value) ? (value as FileFieldValue[]) : []}
          onChange={onChange}
          accept={c.fileAccept}
          maxFiles={c.maxFiles ?? 1}
          maxSizeMb={c.maxFileSizeMb}
          surface={surface}
        />
      );
    default:
      return null;
  }
}
